import { useLayoutEffect, useRef, useState, type CSSProperties } from "react";

export type ProcessStep = {
  title: string;
  body: string;
};

type Props = {
  steps: ProcessStep[];
};

function segmentProgress(progress: number, index: number, count: number) {
  if (count < 2) return progress;
  const start = index / (count - 1);
  const span = 1 / (count - 1);
  return Math.min(1, Math.max(0, (progress - start) / span));
}

function stepReached(progress: number, index: number, count: number) {
  if (count < 2) return progress > 0;
  const threshold = index / (count - 1);
  return progress >= threshold - 0.02;
}

/**
 * Scroll-linked path between process steps. The dashed line is drawn from the
 * scroll position. Without JavaScript, or with reduced motion, the path and
 * every step render in their finished state. Copy stays in the document.
 */
export function ProcessPath({ steps }: Props) {
  const listRef = useRef<HTMLOListElement>(null);
  const [progress, setProgress] = useState(1);
  const [motionOn, setMotionOn] = useState(false);
  const [animate, setAnimate] = useState(false);
  const motionOnRef = useRef(false);

  useLayoutEffect(() => {
    const root = listRef.current;
    if (!root) return;

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)");
    let frame = 0;

    const apply = () => {
      frame = 0;
      if (reduce.matches) {
        motionOnRef.current = false;
        setMotionOn(false);
        setProgress(1);
        return;
      }
      const viewHeight = window.innerHeight || 1;
      const nodes = root.querySelectorAll<HTMLElement>(".pe-step-index");
      const first = nodes[0]?.getBoundingClientRect();
      const last = nodes[nodes.length - 1]?.getBoundingClientRect();
      let raw = 1;
      if (first && last) {
        const firstCentre = first.top + first.height / 2;
        const lastCentre = last.top + last.height / 2;
        const verticalTravel = lastCentre - firstCentre;
        if (verticalTravel > 24) {
          // The line meets a step as that step crosses the lower part of the screen.
          raw = (viewHeight * 0.9 - firstCentre) / verticalTravel;
        } else {
          const rect = root.getBoundingClientRect();
          const travel = Math.max(rect.height, viewHeight * 0.42);
          raw = (viewHeight * 0.75 - rect.top) / travel;
        }
      }
      const next = Math.min(1, Math.max(0, Math.round(raw * 100) / 100));
      if (!motionOnRef.current) {
        motionOnRef.current = true;
        setMotionOn(true);
      }
      setProgress((current) => (current === next ? current : next));
    };

    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(apply);
    };

    apply();
    const arm = window.requestAnimationFrame(() => setAnimate(true));

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        if (entry.isIntersecting) {
          apply();
          window.addEventListener("scroll", onScroll, { passive: true });
          return;
        }
        window.removeEventListener("scroll", onScroll);
        if (reduce.matches) return;
        const rect = root.getBoundingClientRect();
        if (!motionOnRef.current) {
          motionOnRef.current = true;
          setMotionOn(true);
        }
        setProgress(rect.bottom <= 80 ? 1 : 0);
      },
      { rootMargin: "15% 0px 15% 0px", threshold: 0 },
    );

    observer.observe(root);
    window.addEventListener("resize", onScroll, { passive: true });
    reduce.addEventListener("change", apply);

    return () => {
      window.cancelAnimationFrame(arm);
      if (frame) window.cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      reduce.removeEventListener("change", apply);
    };
  }, []);

  return (
    <ol
      ref={listRef}
      className="pe-steps"
      data-motion={motionOn ? "on" : undefined}
      data-animate={animate ? "on" : undefined}
    >
      {steps.map((step, index) => {
        const reached = stepReached(progress, index, steps.length);
        const segment =
          index < steps.length - 1 ? segmentProgress(progress, index, steps.length) : 1;
        const number = String(index + 1).padStart(2, "0");
        return (
          <li key={step.title} className={reached ? "pe-step is-reached" : "pe-step"}>
            {index < steps.length - 1 ? (
              <span
                className="pe-step-connector"
                aria-hidden="true"
                style={{ "--seg": segment } as CSSProperties}
              >
                <span className="pe-step-connector-draw" />
              </span>
            ) : null}
            <div className="pe-step-body">
              <span className="pe-step-index">{number}</span>
              <div className="pe-step-copy">
                <h3 className="pe-step-title font-serif text-xl leading-tight lg:text-2xl">
                  {step.title}
                </h3>
                <p className="mt-2 max-w-[36ch] text-sm leading-relaxed text-pretty text-charcoal/75 lg:text-[0.9375rem]">
                  {step.body}
                </p>
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
